/**
 * AddStopDialog - Bottom sheet for adding custom delivery stops
 *
 * Uses Google Places textSearch to find locations.
 * Location-biased to Glasgow area with 100km radius.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { Search, MapPin, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { loadMapsApi } from '../../utils/geocoding'

// Glasgow centre for location bias
const GLASGOW = { lat: 55.86, lng: -4.25 }
const BIAS_RADIUS = 100000 // 100km in metres

export function AddStopDialog({ open, onOpenChange, onAddStop }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState(null)
  const debounceRef = useRef(null)
  const inputRef = useRef(null)

  // Focus input when sheet opens
  useEffect(() => {
    if (open) {
      // Small delay to allow sheet animation
      const t = setTimeout(() => inputRef.current?.focus(), 300)
      return () => clearTimeout(t)
    }
    // Reset state when closing
    setQuery('')
    setResults([])
    setError(null)
  }, [open])

  const searchPlaces = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setResults([])
      return
    }

    setSearching(true)
    setError(null)

    try {
      await loadMapsApi()

      const { Place } = await google.maps.importLibrary('places')
      const request = {
        textQuery: searchQuery,
        locationBias: new google.maps.Circle({
          center: GLASGOW,
          radius: BIAS_RADIUS
        }),
        fields: ['displayName', 'formattedAddress', 'location'],
        maxResultCount: 8
      }

      const { places } = await Place.searchByText(request)

      setResults(places.map(place => ({
        name: place.displayName,
        address: place.formattedAddress,
        lat: place.location.lat(),
        lng: place.location.lng()
      })))
    } catch (err) {
      console.error('Places search failed:', err)
      setError('Search failed. Please try again.')
      setResults([])
    } finally {
      setSearching(false)
    }
  }, [])

  const handleInputChange = (e) => {
    const value = e.target.value
    setQuery(value)

    // Debounce search
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => searchPlaces(value), 400)
  }

  const handleSelectPlace = (place) => {
    const customStop = {
      id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      customerName: place.name,
      branchName: null,
      address: place.address,
      items: [],
      lat: place.lat,
      lng: place.lng,
      isCustomStop: true
    }

    onAddStop(customStop)
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh] rounded-t-xl pb-safe">
        <SheetHeader className="mb-4">
          <SheetTitle>Add Stop</SheetTitle>
          <SheetDescription>Search for a location to add to your route</SheetDescription>
        </SheetHeader>

        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            ref={inputRef}
            value={query}
            onChange={handleInputChange}
            placeholder="Search for a place..."
            className="pl-9"
          />
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600 mb-3">{error}</p>
        )}

        {/* Loading */}
        {searching && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        )}

        {/* Results */}
        {!searching && results.length > 0 && (
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {results.map((place, i) => (
              <button
                key={i}
                className="w-full flex items-start gap-3 p-3 rounded-lg text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
                onClick={() => handleSelectPlace(place)}
              >
                <MapPin className="w-5 h-5 mt-0.5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{place.name}</p>
                  <p className="text-sm text-gray-500 truncate">{place.address}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!searching && query.length >= 3 && results.length === 0 && !error && (
          <p className="text-sm text-gray-500 text-center py-8">No results found</p>
        )}

        {/* Hint */}
        {!searching && query.length < 3 && results.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            Type at least 3 characters to search
          </p>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default AddStopDialog
